import React, { useState } from 'react';
import { Button } from './ui';
import { usePWA } from '../hooks/usePWA';

interface PWAInstallPromptProps {
  variant?: 'banner' | 'button' | 'inline';
  className?: string;
}

export function PWAInstallPrompt({ variant = 'banner', className = '' }: PWAInstallPromptProps) {
  const {
    canInstall,
    isInstalled,
    installApp,
    updateServiceWorker,
  } = usePWA();

  const [isInstalling, setIsInstalling] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);

  // Écouter les événements de mise à jour du Service Worker
  React.useEffect(() => {
    const handleUpdateAvailable = () => {
      setShowUpdatePrompt(true);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable);
    return () => window.removeEventListener('sw-update-available', handleUpdateAvailable);
  }, []);

  const handleInstall = async () => {
    setIsInstalling(true);
    try {
      const installed = await installApp();
      if (installed) {
        console.log('[PWA] App installée avec succès');
      }
    } catch (error) {
      console.error('[PWA] Erreur lors de l\'installation:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleUpdate = async () => {
    try {
      await updateServiceWorker();
    } catch (error) {
      console.error('[PWA] Erreur lors de la mise à jour:', error);
    }
  };

  // Ne pas afficher si déjà installé
  if (isInstalled) {
    return null;
  }

  // Afficher le prompt de mise à jour
  if (showUpdatePrompt) {
    return (
      <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-800">
              Mise à jour disponible
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Une nouvelle version de l'application est disponible.
            </p>
          </div>
          <div className="flex space-x-2 ml-4">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowUpdatePrompt(false)}
            >
              Plus tard
            </Button>
            <Button
              size="sm"
              onClick={handleUpdate}
            >
              Mettre à jour
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Afficher le prompt d'installation selon le variant
  if (!canInstall) {
    return null;
  }

  if (variant === 'banner') {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-sm font-medium text-green-800">
              Installer l'application
            </h3>
            <p className="text-sm text-green-700 mt-1">
              Installez DigiManager pour un accès rapide et hors ligne.
            </p>
          </div>
          <div className="flex space-x-2 ml-4">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {}}
            >
              Plus tard
            </Button>
            <Button
              size="sm"
              onClick={handleInstall}
              isLoading={isInstalling}
              disabled={isInstalling}
            >
              Installer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'button') {
    return (
      <Button
        onClick={handleInstall}
        isLoading={isInstalling}
        disabled={isInstalling}
        className={className}
      >
        📱 Installer l'app
      </Button>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={`text-center py-6 ${className}`}>
        <div className="mx-auto max-w-sm">
          <div className="text-4xl mb-4">📱</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Utiliser comme application
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Installez DigiManager sur votre appareil pour un accès rapide et des fonctionnalités hors ligne.
          </p>
          <Button
            onClick={handleInstall}
            isLoading={isInstalling}
            disabled={isInstalling}
            className="w-full"
          >
            Installer l'application
          </Button>
        </div>
      </div>
    );
  }

  return null;
}

// Composant pour afficher le statut de connexion
interface ConnectionStatusProps {
  className?: string;
}

export function ConnectionStatus({ className = '' }: ConnectionStatusProps) {
  const { isOnline } = usePWA();

  if (isOnline) {
    return null; // Ne pas afficher quand en ligne
  }

  return (
    <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-3 ${className}`}>
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium text-yellow-800">
            Mode hors ligne
          </p>
          <p className="text-sm text-yellow-700">
            Certaines fonctionnalités peuvent être limitées.
          </p>
        </div>
      </div>
    </div>
  );
}

// Composant pour les actions PWA dans les paramètres
interface PWAActionsProps {
  className?: string;
}

export function PWAActions({ className = '' }: PWAActionsProps) {
  const {
    isInstalled,
    isServiceWorkerReady,
    clearCache,
    getCacheInfo,
  } = usePWA();

  const [cacheInfo, setCacheInfo] = useState<any[]>([]);
  const [isLoadingCache, setIsLoadingCache] = useState(false);

  const handleShowCacheInfo = async () => {
    setIsLoadingCache(true);
    try {
      const info = await getCacheInfo();
      setCacheInfo(info);
    } catch (error) {
      console.error('Erreur lors de la récupération des infos de cache:', error);
    } finally {
      setIsLoadingCache(false);
    }
  };

  const handleClearCache = async () => {
    try {
      await clearCache();
      setCacheInfo([]);
    } catch (error) {
      console.error('Erreur lors du vidage du cache:', error);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-900">Application installée</p>
          <p className="text-sm text-gray-500">
            {isInstalled ? '✅ Oui' : '❌ Non'}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-900">Service Worker</p>
          <p className="text-sm text-gray-500">
            {isServiceWorkerReady ? '✅ Actif' : '❌ Inactif'}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleShowCacheInfo}
          isLoading={isLoadingCache}
          disabled={isLoadingCache}
          className="w-full"
        >
          Voir le cache
        </Button>

        {cacheInfo.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm text-gray-600">
              {cacheInfo.map((cache, index) => (
                <div key={index} className="flex justify-between">
                  <span>{cache.name}</span>
                  <span>{cache.size} éléments</span>
                </div>
              ))}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleClearCache}
              className="w-full"
            >
              Vider le cache
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}